import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

export interface CustomerBalanceItem {
  ma_kh: string;
  tk?: string;
}

interface CustomerBalanceResult extends CustomerBalanceItem {
  balance?: string | number;
  du_cn?: string | number;
  so_du?: string | number;
}

interface UseCustomerBalanceOptions {
  ngayCt?: string;
  items: CustomerBalanceItem[];
}

const balanceKey = (maKh: string, tk?: string) => `${maKh}:${tk || ''}`;

const responseItems = (data: unknown): CustomerBalanceResult[] => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  const response = data as Record<string, unknown>;
  if (Array.isArray(response.results)) return response.results as CustomerBalanceResult[];
  if (Array.isArray(response.data)) return response.data as CustomerBalanceResult[];
  if (response.data && typeof response.data === 'object') {
    const nested = response.data as Record<string, unknown>;
    if (Array.isArray(nested.results)) return nested.results as CustomerBalanceResult[];
  }
  return [];
};

export function useCustomerBalance({ ngayCt, items }: UseCustomerBalanceOptions) {
  const { entity } = useAuth();
  const [balances, setBalances] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const requestItems = useMemo(() => {
    const unique = new Map<string, CustomerBalanceItem>();
    items.forEach(item => {
      if (item.ma_kh) unique.set(balanceKey(item.ma_kh, item.tk), item);
    });
    return [...unique.values()];
  }, [items]);
  const requestKey = JSON.stringify(requestItems);

  useEffect(() => {
    if (!entity?.slug || !ngayCt || requestItems.length === 0) {
      setBalances({});
      return;
    }

    let active = true;
    setIsLoading(true);
    api
      .post(`/entities/${entity.slug}/erp/${QUERY_KEYS.SO_DU_CONG_NO}/`, {
        ngay_ct: ngayCt,
        items: requestItems
      })
      .then(response => {
        if (!active) return;
        const next: Record<string, string> = {};
        responseItems(response.data).forEach(item => {
          const value = item.so_du ?? item.du_cn ?? item.balance ?? 0;
          next[balanceKey(item.ma_kh, item.tk)] = String(value);
          if (!item.tk) next[balanceKey(item.ma_kh)] = String(value);
        });
        setBalances(next);
      })
      .catch(() => {
        if (active) setBalances({});
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [entity?.slug, ngayCt, requestItems, requestKey]);

  const getCustomerBalance = useCallback(
    (customerUuid: string, accountUuid?: string) =>
      balances[balanceKey(customerUuid, accountUuid)] ?? balances[balanceKey(customerUuid)] ?? '0.00',
    [balances]
  );

  return { getCustomerBalance, isLoading };
}
